import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, signOut, sendPasswordResetEmail, signInWithPopup, signInWithRedirect, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';

// // Lucide 'circle-user-round' icon
// const CircleUserRoundIcon = () => (
//   <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 10 }}>
//     <circle cx="12" cy="8" r="5" />
//     <path d="M20 21a8 8 0 0 0-16 0" />
//     <circle cx="12" cy="12" r="10" stroke="#1976d2" strokeWidth="1.5" fill="none" />
//   </svg>
// );

const linkStyle: React.CSSProperties = { color: '#1976d2', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', textDecoration: 'none' };
const buttonStyle: React.CSSProperties = { width: '100%', marginBottom: 8, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '10px 0', fontWeight: 600, fontSize: 16, cursor: 'pointer' };
const inputStyle: React.CSSProperties = { width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', marginBottom: 12, fontSize: 16, boxSizing: 'border-box' };

// Social icons (SVG, inline, no dependencies)
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#000"/>
  </svg>
);

const CustomAuth: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const getFriendlyError = (error: any) => {
    if (!error || !error.code) return error.message || String(error);
    switch (error.code) {
      case 'auth/email-already-in-use':
        return t('auth.errorEmailInUse');
      case 'auth/invalid-email':
        return t('auth.errorInvalidEmail');
      case 'auth/weak-password':
        return t('auth.errorWeakPassword');
      case 'auth/popup-closed-by-user':
        return t('auth.errorPopupClosed') || 'Sign-in popup was closed. Please try again.';
      case 'auth/popup-blocked':
        return t('auth.errorPopupBlocked') || 'Sign-in popup was blocked. Please allow popups for this site and try again.';
      case 'auth/redirect-cancelled-by-user':
        return t('auth.errorRedirectCancelled') || 'Sign-in was cancelled. Please try again.';
      case 'auth/redirect-operation-pending':
        return t('auth.errorRedirectPending') || 'Sign-in is in progress. Please wait...';
      case 'auth/unauthorized-domain':
        return t('auth.errorUnauthorizedDomain') || 'This domain is not authorized for sign-in. Please contact support.';
      case 'auth/operation-not-allowed':
        return t('auth.errorOperationNotAllowed') || 'Google sign-in is not enabled. Please contact support.';
      case 'auth/network-request-failed':
        return t('auth.errorNetworkFailed') || 'Network error. Please check your connection and try again.';
      case 'auth/too-many-requests':
        return t('auth.errorTooManyRequests') || 'Too many sign-in attempts. Please try again later.';
      default:
        return t('auth.errorDefault') || `Authentication error: ${error.code}`;
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      await signOut(auth);
      setMessage(t('auth.verificationSent'));
    } catch (error: any) {
      setMessage(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    console.log('Form submitted!');
    e.preventDefault();
    setMessage('');
    setLoading(true);
    console.log('Attempting to sign in with email:', email);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Sign in successful:', userCredential.user);
      if (!userCredential.user.emailVerified) {
        console.log('Email not verified, signing out');
        setMessage(t('auth.verifyEmail'));
        setUser(userCredential.user);
        await signOut(auth);
      } else {
        console.log('Email verified, setting user');
        setUser(userCredential.user);
        setMessage(t('auth.loggedIn'));
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (user) {
      await sendEmailVerification(user);
      setMessage(t('auth.verificationSent'));
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(t('auth.resetSent'));
    } catch (error: any) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setMessage('');
    setLoading(true);
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      setMessage(t('auth.loggedIn') || 'Successfully logged in!');
    } catch (error: any) {
      console.error('Google sign in error:', error);
      setMessage(getFriendlyError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setMessage('');
    setLoading(true);
    
    try {
      const provider = new OAuthProvider('apple.com');
      
      // Add scopes for email and name
      provider.addScope('email');
      provider.addScope('name');
      
      const result = await signInWithPopup(auth, provider);
      
      // Handle private email from Apple with careful merging
      if (result.user) {
        const email = result.user.email;
        const isPrivateEmail = email && email.includes('privaterelay.appleid.com');
        
        if (isPrivateEmail) {
          console.log('⚠️ User used private email:', email);
          setMessage('Private email detected. Your account is linked to your Apple ID for security.');
        } else {
          console.log('✅ User shared real email:', email);
        }
        
        setUser(result.user);
        setMessage(t('auth.loggedIn') || 'Successfully logged in!');
      }
    } catch (error: any) {
      console.error('❌ Apple sign in error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Enhanced error handling for Apple Sign-In with private email guidance
      if (error.code === 'auth/operation-not-allowed') {
        setMessage('Apple Sign-In is not configured in Firebase. Please contact support.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setMessage('An account already exists with this email using a different sign-in method. Please use "Share My Email" option.');
      } else if (error.code === 'auth/invalid-credential') {
        setMessage('Invalid Apple Sign-In credentials. Please try again with "Share My Email" option.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setMessage('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setMessage('This domain is not authorized for Apple Sign-In. Please contact support.');
      } else if (error.code === 'auth/email-already-in-use') {
        setMessage('This email is already registered. Please use "Share My Email" to link your Apple ID.');
      } else {
        setMessage(getFriendlyError(error));
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div style={{ maxWidth: 400, margin: '0 auto', padding: 24, fontFamily: 'Inter, Arial, sans-serif' }}>
      {/* Header: icon + text, centered, no duplicate */}
      <div style={{ display: 'flex', alignItems: 'left', justifyContent: 'left', marginBottom: 10 }}>
        {/* <CircleUserRoundIcon /> */}
        <span style={{ fontWeight: 700, fontSize: 20 }}>
          {mode === 'login' ? t('auth.login') : mode === 'signup' ? t('auth.signup') : t('auth.forgotPassword')}
        </span>
      </div>
      {mode === 'forgot' ? (
        <form onSubmit={handleForgotPassword}>
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={inputStyle}
          />
          <button type="submit" style={buttonStyle} disabled={loading}>
            {t('auth.sendResetLink')}
          </button>
          <span
            style={{ color: '#1976d2', cursor: 'pointer', fontSize: 15, marginTop: 8, display: 'inline-block' }}
            onClick={() => { setMode('login'); setMessage(''); }}
          >
            {t('auth.backToLogin')}
          </span>
          {message && (
            <div style={{ marginTop: 12, color: message.includes('sent') ? 'green' : 'red', textAlign: 'center', fontWeight: message.includes('sent') ? 500 : undefined }}>{message}</div>
          )}
        </form>
      ) : mode === 'signup' ? (
        <form onSubmit={handleSignUp}>
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={inputStyle}
          />
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
            />
            <span
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
              onClick={() => setShowPassword(v => !v)}
              title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {/* Simple eye icon */}
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                {showPassword ? (
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Zm11 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="#888" strokeWidth="2" />
                ) : (
                  <>
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.8 21.8 0 0 1 5.06-6.06M9.53 9.53A3 3 0 0 0 12 15a3 3 0 0 0 2.47-5.47" stroke="#888" strokeWidth="2" />
                    <path d="M1 1l22 22" stroke="#888" strokeWidth="2" />
                  </>
                )}
              </svg>
            </span>
          </div>
          <button type="submit" style={buttonStyle} disabled={loading}>
            {t('auth.signup')}
          </button>
          <div style={{ textAlign: 'center', margin: '8px 0 8px 0', fontSize: 15 }}>
            <span>
              {t('auth.alreadyHaveAccount')} {' '}
              <span
                style={{ color: '#1976d2', cursor: 'pointer', fontWeight: 500 }}
                onClick={() => { setMode('login'); setMessage(''); }}
              >
                {t('auth.login')}
              </span>
            </span>
          </div>
          {message && message.includes('Verification email sent') && (
            <div style={{ marginTop: 12, color: 'green', textAlign: 'center', fontWeight: 500 }}>{t('auth.verificationSent')}</div>
          )}
          {message && !message.includes('Verification email sent') && (
            <div style={{ marginTop: 12, color: 'red', textAlign: 'center' }}>{message}</div>
          )}
        </form>
      ) : (
        <form onSubmit={handleSignIn}>
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={inputStyle}
          />
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={inputStyle}
            />
            <span
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
              onClick={() => setShowPassword(v => !v)}
              title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {/* Simple eye icon */}
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                {showPassword ? (
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Zm11 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="#888" strokeWidth="2" />
                ) : (
                  <>
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a21.8 21.8 0 0 1 5.06-6.06M9.53 9.53A3 3 0 0 0 12 15a3 3 0 0 0 2.47-5.47" stroke="#888" strokeWidth="2" />
                    <path d="M1 1l22 22" stroke="#888" strokeWidth="2" />
                  </>
                )}
              </svg>
            </span>
          </div>
          {mode === 'login' && (
            <span
              style={{ color: '#1976d2', cursor: 'pointer', float: 'right', marginBottom: 8, fontSize: 15 }}
              onClick={() => { setMode('forgot'); setMessage(''); }}
            >
              {t('auth.forgotPasswordQuestion')}
            </span>
          )}
          <button 
            type="submit" 
            style={buttonStyle} 
            disabled={loading}
            onClick={() => console.log('Login button clicked')}
          >
            {t('auth.login')}
          </button>
          <div style={{ textAlign: 'center', margin: '8px 0 8px 0', fontSize: 15 }}>
            <span>
              {t('auth.needAccount')} {' '}
              <span
                style={{ color: '#1976d2', cursor: 'pointer', fontWeight: 500 }}
                onClick={() => { setMode('signup'); setMessage(''); }}
              >
                {t('auth.signup')}
              </span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
            <span style={{ margin: '0 12px', color: '#888', fontWeight: 500 }}>{t('auth.or')}</span>
            <div style={{ flex: 1, height: 1, background: '#e0e0e0' }} />
          </div>
                     {/* Social login buttons, all same style, only icon colored, text always black */}
           <button 
             type="button" 
             onClick={handleGoogleSignIn}
             style={{ ...buttonStyle, background: '#fff', color: '#222', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500 }}
           >
             <GoogleIcon /> {t('auth.continueWith', { provider: 'Google' })}
           </button>
           {import.meta.env.DEV && (
             <div style={{ fontSize: 11, color: '#666', textAlign: 'center', marginBottom: 8, fontStyle: 'italic' }}>
               Redirect tests work best on deployed site (https://prod.spinnergames.fun)
             </div>
           )}
           {import.meta.env.DEV && (
             <button 
               type="button" 
               onClick={async () => {
                 setMessage('');
                 setLoading(true);
                 console.log('=== Testing Google Redirect ===');
                 console.log('Current auth state:', auth.currentUser);
                 console.log('Auth domain:', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN);
                 console.log('Current URL:', window.location.href);
                 console.log('User agent:', navigator.userAgent);
                 console.log('Is popup blocked?', window.opener === null);
                 
                 try {
                   const provider = new GoogleAuthProvider();
                   console.log('Google provider created for redirect test');
                   
                   // Add some custom parameters for debugging
                   provider.setCustomParameters({
                     prompt: 'select_account'
                   });
                   
                   // Store current URL for debugging
                   localStorage.setItem('redirect_test_url', window.location.href);
                   localStorage.setItem('redirect_test_time', Date.now().toString());
                   
                   console.log('About to call signInWithRedirect...');
                   console.log('Provider:', provider);
                   console.log('Auth instance:', auth);
                   
                   await signInWithRedirect(auth, provider);
                   console.log('Redirect initiated successfully');
                   // Note: Code after this line won't execute due to redirect
                 } catch (error: any) {
                   console.error('Google redirect error:', error);
                   console.error('Error code:', error.code);
                   console.error('Error message:', error.message);
                   console.error('Error stack:', error.stack);
                   setMessage(getFriendlyError(error));
                   setLoading(false);
                 }
               }}
               style={{ ...buttonStyle, background: '#f0f0f0', color: '#333', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, marginTop: 8 }}
             >
               <GoogleIcon /> Test Google Redirect (Dev Only)
             </button>
           )}
           <button 
             type="button" 
             onClick={handleAppleSignIn}
             style={{ ...buttonStyle, background: '#fff', color: '#222', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500 }}
           >
             <AppleIcon /> {t('auth.continueWith', { provider: 'Apple' })}
           </button>
           <div style={{ fontSize: 11, color: '#666', textAlign: 'center', marginTop: 4, fontStyle: 'italic' }}>
             {t('auth.appleSignInTip')}
           </div>
           {import.meta.env.DEV && (
             <button 
               type="button" 
               onClick={async () => {
                 setMessage('');
                 setLoading(true);
                 console.log('=== Testing Apple Redirect ===');
                 console.log('Current auth state:', auth.currentUser);
                 console.log('Auth domain:', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN);
                 console.log('Current URL:', window.location.href);
                 console.log('User agent:', navigator.userAgent);
                 console.log('Is popup blocked?', window.opener === null);
                 
                 try {
                   const provider = new OAuthProvider('apple.com');
                   console.log('Apple provider created for redirect test');
                   
                   // Store current URL for debugging
                   localStorage.setItem('apple_redirect_test_url', window.location.href);
                   localStorage.setItem('apple_redirect_test_time', Date.now().toString());
                   
                   console.log('About to call signInWithRedirect for Apple...');
                   console.log('Provider:', provider);
                   console.log('Auth instance:', auth);
                   
                   await signInWithRedirect(auth, provider);
                   console.log('Apple redirect initiated successfully');
                   // Note: Code after this line won't execute due to redirect
                 } catch (error: any) {
                   console.error('Apple redirect error:', error);
                   console.error('Error code:', error.code);
                   console.error('Error message:', error.message);
                   console.error('Error stack:', error.stack);
                   setMessage(getFriendlyError(error));
                   setLoading(false);
                 }
               }}
               style={{ ...buttonStyle, background: '#f0f0f0', color: '#333', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, marginTop: 8 }}
             >
               <AppleIcon /> Test Apple Redirect (Dev Only)
             </button>
           )}
          {message && (
            <div style={{ marginTop: 12, color: message.includes('sent') ? 'green' : 'red', textAlign: 'center', fontWeight: message.includes('sent') ? 500 : undefined }}>{message}</div>
          )}
          
          {import.meta.env.DEV && (
            <div style={{ marginTop: 16, padding: 12, background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 4, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Debug Info (Dev Only):</div>
              <div>Current User: {auth.currentUser ? auth.currentUser.email : 'None'}</div>
              <div>Auth Domain: {import.meta.env.VITE_FIREBASE_AUTH_DOMAIN}</div>
              <div>Current URL: {window.location.href}</div>
              <div>URL Hash: {window.location.hash}</div>
              <div>URL Search: {window.location.search}</div>
              <div>Google Redirect Test URL: {localStorage.getItem('redirect_test_url') || 'None'}</div>
              <div>Google Redirect Test Time: {localStorage.getItem('redirect_test_time') ? new Date(parseInt(localStorage.getItem('redirect_test_time')!)).toLocaleString() : 'None'}</div>
              <div>Apple Redirect Test URL: {localStorage.getItem('apple_redirect_test_url') || 'None'}</div>
              <div>Apple Redirect Test Time: {localStorage.getItem('apple_redirect_test_time') ? new Date(parseInt(localStorage.getItem('apple_redirect_test_time')!)).toLocaleString() : 'None'}</div>
              <button 
                onClick={() => {
                  localStorage.removeItem('redirect_test_url');
                  localStorage.removeItem('redirect_test_time');
                  localStorage.removeItem('apple_redirect_test_url');
                  localStorage.removeItem('apple_redirect_test_time');
                  window.location.reload();
                }}
                style={{ marginTop: 8, padding: '4px 8px', fontSize: 10, background: '#dc3545', color: 'white', border: 'none', borderRadius: 2, cursor: 'pointer' }}
              >
                Clear Redirect Test Data
              </button>
            </div>
          )}

        </form>
      )}
      {user && !user.emailVerified && (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <button onClick={handleResendVerification} style={linkStyle}>
            {t('auth.resendVerification')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomAuth; 